import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import AuthLayout from './layouts/AuthLayout';
import MainLayout from './layouts/MainLayout';
import HomePage from './pages/HomePage';
import Dashboard from './modules/reports/Dashboard';
import SignIn from './pages/SignIn';
import Unauthorized from './pages/Unauthorized';
import PrivacyPage from './pages/legal/PrivacyPage';
import TermsPage from './pages/legal/TermsPage';
import DeleteAccountPage from './pages/legal/DeleteAccountPage';
import './index.css';

// LMS routes - new @lms structure
import { lmsRoutes } from './modules/lms/LMSRoutes';

// School module - now integrated into admin module
import { 
  SchoolDashboard, 
  SchoolDetailPage
} from './modules/school';

// Class module - now integrated into admin module
import { 
  ClassDashboard, 
  ClassDetailPage
} from './modules/class';

// Activate Book module - now integrated into admin module
import { 
  ActivateBookPage
} from './modules/activate-book';

// Admin routes - new admin structure
import AdminLayout from './modules/admin/AdminLayout';
import AdminDashboard from './modules/admin/AdminDashboard';

// Admin modules (moved from LMS)
import OrganizationPage from './prototypes/lms/organization/OrganizationPage';

// School Teacher module
import { SchoolTeacherDashboard } from './modules/school-teacher';

// School Student module
import { SchoolStudentDashboard } from './modules/school-student';

// User Asset module
import { UserAssetDashboard, UserAssetDetailPage } from './modules/user-asset';
// Activation report module
import { ActivationReportPage } from './modules/activation-report';

// User module
import { UserDashboard } from './modules/user';

// Reader Version module
import { ReaderVersionDashboard } from './modules/reader-version';

// LCMS routes - keep existing LCMS functionality
import LCMSLayout from './modules/lcms/LCMSLayout';
import LCMSDashboard from './modules/lcms/LCMSDashboard';
import NewCourse from './modules/lcms/content-courses/NewCourse';
import { CoursesListPage } from './modules/lcms/content-courses/CoursesListPage';
import ContentBank from './modules/lcms/content/ContentBank';
import Metadata from './modules/lcms/metadata/Metadata';
import ActiveProjects from './modules/lcms/projects/ActiveProjects';
import ArchivedProjects from './modules/lcms/projects/ArchivedProjects';
import QuestionBank from './modules/lcms/banks/QuestionBank';
import MediaBank from './modules/lcms/banks/MediaBank';
import { LanguageProvider } from './i18n/LanguageContext';
import { ContentCourseDetailPage } from './modules/lcms/content-courses/ContentCourseDetailPage';

// Reports routes - new @reports structure
import { ReportsLayout, CourseAnalytics, StudentAnalytics, LMSReports } from './modules/reports';

// Learn module - new @learn structure for learners
import {
  LearnDashboard,
  CoursePlayer,
  MyCoursesPage,
  InProgressCoursesPage,
  CertificatesPage
} from './modules/learn';
import LearnLayout from './modules/learn/LearnLayout';

// Additional Reports Components
import TrainingPlanImplementationReport from './modules/reports/TrainingPlanImplementationReport';
import { MonthlyTrainingReport, RegionalTrainingStatisticsReport } from './modules/reports';
import bookRoutes from './modules/book/BookRoutes';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 phút - data được coi là fresh
      gcTime: 30 * 60 * 1000, // 30 phút - data được giữ trong cache
      refetchOnWindowFocus: false, // Không refetch khi focus window
      refetchOnMount: false, // Không refetch khi component mount
      refetchOnReconnect: false, // Không refetch khi reconnect
      retry: 1, // Chỉ retry 1 lần
      retryDelay: 1000, // Delay 1 giây trước khi retry
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <NuqsAdapter>
          <Router>
            <AuthProvider>
              <Routes>
                {/* Auth Routes - Sử dụng AuthLayout */}
                <Route path="/auth" element={<AuthLayout />}>
                  <Route path="signin" element={<SignIn />} />
                </Route>

                {/* Trang công khai bắt buộc cho store — không bọc PrivateRoute */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />

                {/* Learn Routes - Using LearnLayout với PrivateRoute */}
                <Route path="/learn" element={
                  <PrivateRoute>
                    <LearnLayout />
                  </PrivateRoute>
                }>
                  <Route index element={<LearnDashboard />} />
                  <Route path="my-courses" element={<MyCoursesPage />} />
                  <Route path="in-progress" element={<InProgressCoursesPage />} />
                  <Route
                    path="completed"
                    element={<div>Completed Courses Page (To be implemented)</div>}
                  />
                  <Route path="certificates" element={<CertificatesPage />} />
                  <Route path="course/:courseId" element={<CoursePlayer />} />
                  <Route path="course/:courseId/lesson/:lessonId" element={<CoursePlayer />} />
                </Route>

                {/* Internal Admin Routes - Tất cả sử dụng MainLayout với PrivateRoute */}
                <Route path="/" element={
                  <PrivateRoute requiredRoles={['admin']}>
                    <MainLayout />
                  </PrivateRoute>
                }>
                  <Route index element={<HomePage />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Admin Routes - New admin structure */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />

                    {/* Human Resources Routes */}
                    <Route path="users" element={<UserDashboard />} />
                    <Route path="school-teachers" element={<SchoolTeacherDashboard />} />
                    <Route path="school-students" element={<SchoolStudentDashboard />} />
                    <Route path="organization" element={<OrganizationPage />} />

                    {/* School Management Routes - Moved from standalone module */}
                    <Route path="school" element={<SchoolDashboard />} />
                    <Route path="school/:schoolId" element={<SchoolDetailPage />} />

                    {/* Class Management Routes - Moved from standalone module */}
                    <Route path="class" element={<ClassDashboard />} />
                    <Route path="class/:classId" element={<ClassDetailPage />} />

                    {/* Activate Book Management Routes - Moved from standalone module */}
                    <Route path="activate-book" element={<ActivateBookPage />} />

                    {/* User Asset Management Routes */}
                    <Route path="user-assets" element={<UserAssetDashboard />} />
                    <Route path="user-assets/:id" element={<UserAssetDetailPage />} />
                    <Route path="activation-report" element={<ActivationReportPage />} />

                    {/* Reader Version Management Routes */}
                    <Route path="reader-versions" element={<ReaderVersionDashboard />} />
                  </Route>
                  {bookRoutes}

                  {/* Reports Routes - New reports structure */}
                  <Route path="/reports" element={<ReportsLayout />}>
                    <Route index element={<Navigate to="/reports/training/monthly" replace />} />

                    {/* Training Reports Routes */}
                    <Route path="training/monthly" element={<MonthlyTrainingReport />} />
                    <Route path="training/progress" element={<TrainingPlanImplementationReport />} />
                    <Route
                      path="training/regional-statistics"
                      element={<RegionalTrainingStatisticsReport />}
                    />

                    {/* Analytics Routes */}
                    <Route path="analytics/courses" element={<CourseAnalytics />} />
                    <Route path="analytics/students" element={<StudentAnalytics />} />

                    {/* LMS Reports */}
                    <Route path="lms" element={<LMSReports />} />
                  </Route>

                  {/* LMS Routes - Updated @lms structure */}
                  {lmsRoutes}

                  {/* LCMS Routes - Keep existing functionality */}
                  <Route path="/lcms" element={<LCMSLayout />}>
                    <Route index element={<LCMSDashboard />} />

                    {/* Course Content Routes */}
                    <Route path="course-content" element={<CoursesListPage />} />
                    <Route path="course-content/list" element={<CoursesListPage />} />
                    <Route path="course-content/new" element={<NewCourse />} />
                    <Route path="course-content/:courseId" element={<ContentCourseDetailPage />} />

                    {/* Content & Metadata Routes */}
                    <Route path="content" element={<ContentBank />} />
                    <Route path="metadata" element={<Metadata />} />

                    {/* Project Routes */}
                    <Route path="projects/active" element={<ActiveProjects />} />
                    <Route path="projects/archived" element={<ArchivedProjects />} />

                    {/* Bank Routes */}
                    <Route path="banks/questions" element={<QuestionBank />} />
                    <Route path="banks/media" element={<MediaBank />} />
                  </Route>
                </Route>

                {/* Unauthorized page */}
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Catch all route - Redirect to dashboard if authenticated, otherwise to signin */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AuthProvider>
          </Router>
        </NuqsAdapter>
        <Toaster position="top-right" />
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
